import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { EngagementRequirementsScreen } from '@/components/engagement-requirements/engagement-requirements-screen';
import { DiscoverySuggestionsScreen } from '@/components/engagement-requirements/discovery-suggestions/discovery-suggestions-screen';
import { parseLibrarySearch } from '@/components/requirement-library/library-logic';
import {
  parseCriticalityFilter,
  parseSourceFilter,
  type CriticalityFilter,
  type SourceFilter,
} from '@/components/engagement-requirements/requirements-logic';
import { ReviewRedirectNotice } from '@/components/engagement/review-redirect-notice';
import { SelectionFrame } from '@/components/engagement-requirements/selection-frame';
import type { SelectionTab } from '@/components/engagement-requirements/selection-tabs';

/**
 * Engagement -> Selection (T-0142 / E11-F04, T-0143 / E11-F05).
 *
 * The Selection module of an engagement: Requirements here, then Vendor
 * intelligence at `…/selection/vendor-intelligence/<step>` (T-0267; four
 * steps with a URL each in T-0272), with "Vendor evaluation" shown as a
 * later tab. An old `?tab=vendor-intelligence` link redirects there. The
 * sidebar links here only when the engagement's `selection` module is on,
 * and the API answers `not_found` when it is off, which the screen says
 * plainly.
 *
 * Design reference: the `#requirements` screen of
 * `docs/requirement-library/prototype/selection.js`. The screen itself is
 * `components/engagement-requirements/engagement-requirements-screen.tsx`;
 * this route only owns the URL state so a view can be shared:
 *
 *   ?q=<search>&module=<module id | removed>&criticality=<value>&source=<value>
 *
 * `?load=<load id>` narrows the list to the rows one load brought in (T-0264:
 * "Show them" after applying a library match).
 *
 * `?add=discovery` opens Add from discovery (T-0190) in the same frame:
 * the fourth way to add requirements, beside the library, a spreadsheet
 * and writing one by hand. Leaving it returns to the list.
 *
 * `?from=proposals` means the IV&V review route sent the reader here: an
 * engagement with no IV&V module and Discovery off has no review queue,
 * and a one-line notice above the frame says so.
 */

interface SelectionSearch {
  q?: string;
  module?: string;
  criticality?: CriticalityFilter;
  source?: SourceFilter;
  add?: 'discovery';
  from?: 'proposals';
  load?: string;
  /** The old Vendor intelligence link; it redirects to the step routes. */
  tab?: 'vendor-intelligence';
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute('/engagements/$engagementId/selection')({
  validateSearch: (search: Record<string, unknown>): SelectionSearch => {
    const criticality = parseCriticalityFilter(search.criticality);
    const source = parseSourceFilter(search.source);
    return {
      // An empty `q` or `module` is the same as none (T-0152).
      ...parseLibrarySearch(search),
      ...(criticality !== 'all' ? { criticality } : {}),
      ...(source !== 'any' ? { source } : {}),
      ...(search.add === 'discovery' ? { add: 'discovery' as const } : {}),
      ...(search.from === 'proposals' ? { from: 'proposals' as const } : {}),
      ...(typeof search.load === 'string' && UUID_RE.test(search.load)
        ? { load: search.load }
        : {}),
      ...(search.tab === 'vendor-intelligence' ? { tab: 'vendor-intelligence' as const } : {}),
    };
  },
  beforeLoad: ({ search, params }) => {
    if (search.tab === 'vendor-intelligence') {
      throw redirect({
        to: '/engagements/$engagementId/selection/vendor-intelligence',
        params: { engagementId: params.engagementId },
        replace: true,
      });
    }
  },
  component: SelectionRoute,
});

function SelectionRoute() {
  const { engagementId } = Route.useParams();
  const { q, module, criticality, source, add, from, load } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const onQueryChange = useCallback(
    (next: string) => {
      void navigate({
        search: (prev) => ({ ...prev, q: next.length > 0 ? next : undefined }),
        // Typing must not fill the history with one entry per keystroke.
        replace: true,
      });
    },
    [navigate],
  );
  const onViewChange = useCallback(
    (next: string | null) => {
      void navigate({ search: (prev) => ({ ...prev, module: next ?? undefined }) });
    },
    [navigate],
  );
  const onCriticalityChange = useCallback(
    (next: CriticalityFilter) => {
      void navigate({
        search: (prev) => ({ ...prev, criticality: next === 'all' ? undefined : next }),
        replace: true,
      });
    },
    [navigate],
  );
  const onSourceChange = useCallback(
    (next: SourceFilter) => {
      void navigate({
        search: (prev) => ({ ...prev, source: next === 'any' ? undefined : next }),
        replace: true,
      });
    },
    [navigate],
  );
  const onClearFilters = useCallback(() => {
    void navigate({
      search: (prev) => ({
        ...prev,
        q: undefined,
        criticality: undefined,
        source: undefined,
        load: undefined,
      }),
      replace: true,
    });
  }, [navigate]);
  // Showing one load's rows starts from the whole list: no search, filter or module.
  const onLoadChange = useCallback(
    (next: string | null) => {
      void navigate({
        search: (prev) =>
          next
            ? {
                ...prev,
                q: undefined,
                criticality: undefined,
                source: undefined,
                module: undefined,
                load: next,
              }
            : { ...prev, load: undefined },
      });
    },
    [navigate],
  );

  const onAddFromDiscovery = useCallback(() => {
    void navigate({ search: (prev) => ({ ...prev, add: 'discovery' as const }) });
  }, [navigate]);
  const onOpenRequirements = useCallback(
    (next?: string) => {
      void navigate({
        search: (prev) => ({ ...prev, add: undefined, ...(next ? { q: next } : {}) }),
      });
    },
    [navigate],
  );

  // Vendor intelligence has its own routes; switching to it keeps nothing else.
  const onSelectTab = useCallback(
    (next: SelectionTab) => {
      if (next === 'vendor-intelligence') {
        void navigate({
          to: '/engagements/$engagementId/selection/vendor-intelligence',
          params: { engagementId },
        });
      }
    },
    [navigate, engagementId],
  );

  return (
    <>
      {from === 'proposals' ? <ReviewRedirectNotice target="selection" /> : null}
      <SelectionFrame>
        {add === 'discovery' ? (
          <DiscoverySuggestionsScreen
            key={engagementId}
            engagementId={engagementId}
            onOpenRequirements={onOpenRequirements}
          />
        ) : (
          <EngagementRequirementsScreen
            // A different engagement is a different screen: drop selection and panel.
            key={engagementId}
            engagementId={engagementId}
            query={q ?? ''}
            view={module ?? null}
            criticality={criticality ?? 'all'}
            source={source ?? 'any'}
            onQueryChange={onQueryChange}
            onViewChange={onViewChange}
            onCriticalityChange={onCriticalityChange}
            onSourceChange={onSourceChange}
            onClearFilters={onClearFilters}
            onAddFromDiscovery={onAddFromDiscovery}
            load={load ?? null}
            onLoadChange={onLoadChange}
            onSelectTab={onSelectTab}
          />
        )}
      </SelectionFrame>
    </>
  );
}
